import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { OrderEntry } from '@spartacus/cart/base/root';
import { BaseSiteService } from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { DealerPrice } from 'src/app/model/dealer-price.model';
import { BaseSiteConfigService } from 'src/app/service/base-site/base-site-config.service';
import { CartExtendService } from 'src/app/service/cart/cart-extend.service';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { DealerPriceService } from 'src/app/service/dealer-price/dealer-price.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { CustomProductService } from 'src/app/service/product/custom-product.service';
import { SnahCountryService, WAREHOUSE } from 'src/app/service/snah/snah-countries.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { SAVED_CART_DETAILS, TooltipIconService } from 'src/app/service/tooltip-icon/tooltip-icon.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';

@Component({
  selector: 'app-saved-cart-details',
  templateUrl: './saved-cart-details.component.html',
  styleUrls: ['./saved-cart-details.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SavedCartDetailsComponent implements OnInit, OnDestroy {
  cartId: string;
  order$: Observable<any>;
  showSrp = false;
  srpSubscription$: Subscription;
  productCodesList: string[] = [];
  baseSite$: Observable<string> = this.baseService.getActive();
  skipVariantCategory: string;
  default = 'default';
  hasDealerPrice = true;
  dealerPrices: DealerPrice[];
  dealerPriceCheckerSubscription$: Subscription;
  janCodeSubscription: Subscription;
  shimanoMemoEnabledSubscription: Subscription;
  shimanoMemoCartEditableSubscription: Subscription;
  shimanoMemoCheckoutEditableSubscription: Subscription;
  janCodeEnabled: boolean;
  memoIsTruncated: boolean;
  shimanoMemoEnabled: boolean;
  shimanoMemoCheckoutEditable: boolean;
  shimanoMemoCartEditable: boolean;
  businessType: string;
  isDiscontinuedEnabled: boolean;

  activeCountry$: Observable<string> = this.countryService.getActive();

  tooltipIconConfig$: Observable<any> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) => this.tooltipIconService.getTooltipIconConfig(activeCountry, SAVED_CART_DETAILS))
  );

  showGstExclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_EXCLUSIVE)
    )
  );

  showWarehouseFeature$: Observable<boolean> = this.SnahCountryService.getSnahFeatures(WAREHOUSE);

  constructor(
    private route: ActivatedRoute,
    protected cartExtendService: CartExtendService,
    private componentService: ComponentService,
    private baseService: BaseSiteService,
    private customProductService: CustomProductService,
    private cdr: ChangeDetectorRef,
    private languageService: ShimanoLanguageService,
    private dealerPriceService: DealerPriceService,
    private countryService: ShimanoCountryService,
    private tooltipIconService: TooltipIconService,
    private checkoutServiceExtends: CheckoutServiceExtends,
    private currentBusinesstype: CurrentBusinessType,
    private baseSiteConfigService: BaseSiteConfigService,
    private storageService: StorageService,
    private SnahCountryService: SnahCountryService
  ) {
    this.route.params.pipe(take(1)).subscribe((params) => {
      this.cartId = params.cartId;
    });

    this.customProductService
      .getSkipVariant()
      .pipe(take(1))
      .subscribe((resp) => {
        this.skipVariantCategory = resp[this.baseSiteId]
          ? resp[this.baseSiteId].defaultVariantCategory
          : resp[this.default].defaultVariantCategory;
      });
  }

  get baseSiteId(): string {
    let baseSiteId: string;
    this.baseSite$.pipe(take(1)).subscribe((baseSiteData) => (baseSiteId = baseSiteData));
    return baseSiteId;
  }

  get activeCountry(): string {
    let activeCountry = '';
    this.countryService.getActive().subscribe((country) => (activeCountry = country));
    return activeCountry;
  }

  ngOnInit() {
    this.componentService.setIsSavingCart(false);
    this.storageService.setSessionStorageItem('isSaveCartPage', 'true');
    this.storageService.removeSessionStorageItem('isRestoringCart');
    this.isDiscountinuedStatusEnabled();

    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });

    this.order$ = this.languageService.getActive().pipe(
      switchMap(() => this.cartExtendService.getSavedCartDetails(this.cartId)),
      tap((data) => {
        const productCodes = data.savedCartData.entries.map((entry: OrderEntry) => entry.product.code);
        this.getDealerPrices(productCodes);
      })
    );

    this.dealerPriceCheckerSubscription$ = this.componentService.hasDealerPrice$.subscribe((hasDealerPrice) => {
      this.hasDealerPrice = hasDealerPrice;
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });
    this.businessType = this.currentBusinesstype.getBusinessType();

    this.isJanCodeEnabled();

    this.isShimanoMemoEnabled();

    this.isShimanoMemoCartEditable();

    this.isShimanoMemoCheckoutEditable();
  }

  checkVariant(variants: any) {
    return variants
      .filter((variant: any) => {
        if (variant.code !== this.skipVariantCategory) {
          return variant;
        }
      })
      .slice(0, 3);
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  getImageRendition(image: any) {
    let size: string;
    size = ShimanoUtilMethod.imageRendition(80);
    return ShimanoUtilMethod.newImageFormat(image, size);
  }

  getDealerPrices(productCodes: string[]) {
    this.dealerPriceService
      .getDealerPricesByProductCodes(productCodes)
      .pipe(take(1))
      .subscribe((data) => {
        this.dealerPrices = data.dealerPrices;
        this.cdr.detectChanges();
      });
  }

  dealerPrice(productCode: string): string {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceByProductCode(this.dealerPrices, productCode);
    }
  }

  isJanCodeEnabled = () => {
    this.janCodeSubscription = this.getJanCodeConfig$().subscribe((janCode) => {
      this.janCodeEnabled = janCode === 'true';
      this.cdr.detectChanges();
    });
  };

  getJanCodeConfig$(): Observable<string> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.ean.display.cart.enabled'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs
              .filter((config: any) => config.key === 'shimano.ean.display.cart.enabled')
              .map((res: any) => res.value)[0]
        )
      );
  }

  isShimanoMemoEnabled = () => {
    this.shimanoMemoEnabledSubscription = this.getShimanoMemoConfig$().subscribe((memoEnabled) => {
      this.shimanoMemoEnabled = memoEnabled === 'true';
      this.cdr.detectChanges();
    });
  };

  getShimanoMemoConfig$(): Observable<string> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.memo.enabled'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs.filter((config: any) => config.key === 'shimano.memo.enabled').map((res: any) => res.value)[0]
        )
      );
  }

  isShimanoMemoCartEditable = () => {
    this.shimanoMemoCartEditableSubscription = this.getCartEditableConfig$().subscribe((cartEditable) => {
      this.shimanoMemoCartEditable = cartEditable;
      this.cdr.detectChanges();
    });
  };

  getCartEditableConfig$(): Observable<boolean> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.memo.cart.editable.enabled'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs
              .filter((config: any) => config.key === 'shimano.memo.cart.editable.enabled')
              .map((res: any) => res.value)[0]
        )
      );
  }

  isShimanoMemoCheckoutEditable = () => {
    this.shimanoMemoCheckoutEditableSubscription = this.getCheckoutEditableConfig$().subscribe((checkoutEditable) => {
      this.shimanoMemoCheckoutEditable = checkoutEditable;
      this.cdr.detectChanges();
    });
  };

  getCheckoutEditableConfig$(): Observable<boolean> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.memo.checkout.editable.enabled'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs
              .filter((config: any) => config.key === 'shimano.memo.checkout.editable.enabled')
              .map((res: any) => res.value)[0]
        )
      );
  }

  showMemoTooltip(event: any, tooltip: MatTooltip, ms?: number): void {
    this.isMemoTruncated(event);
    if (event.type === 'click' && this.memoIsTruncated) {
      tooltip.show();
      setTimeout(() => {
        tooltip.hide();
        event.target.blur();
      }, ms);
    }
  }

  isMemoTruncated(event: any): boolean {
    this.memoIsTruncated = event.target.scrollWidth > event.target.offsetWidth ? true : false;
    return this.memoIsTruncated;
  }

  isDiscontinued(status?: string): boolean {
    return status === 'discontinued' && this.isDiscontinuedEnabled;
  }

  isDiscountinuedStatusEnabled(): void {
    this.getDiscountedStatusConfig$()
      .pipe(take(1))
      .subscribe((enabled) => {
        this.isDiscontinuedEnabled = enabled;
      });
  }

  getDiscountedStatusConfig$(): Observable<boolean> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.discontinued.status.enabled'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs
              .filter((config: any) => config.key === 'shimano.discontinued.status.enabled')
              .map((res: any) => res.value)[0]
        )
      );
  }
  ngOnDestroy() {
    if (this.srpSubscription$) {
      this.srpSubscription$.unsubscribe();
    }
    if (this.dealerPriceCheckerSubscription$) {
      this.dealerPriceCheckerSubscription$.unsubscribe();
    }
    if (this.janCodeSubscription) {
      this.janCodeSubscription.unsubscribe();
    }
  }
}
